import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectStage } from './models';
import { baseApi } from '@centerpiece/components';

export const project_stage = baseApi
  .enhanceEndpoints({ addTagTypes: ['ProjectStages', 'ProjectStage'] })
  .injectEndpoints({
    endpoints: builder => ({
      getProjectStages: builder.query<ProjectStage[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'project_stage',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ProjectStage' as const,
                  id,
                })),
                'ProjectStages',
              ]
            : ['ProjectStages'],
      }),
      getProjectStage: builder.query<ProjectStage, string>({
        query: id => ({
          url: `project_stage/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'ProjectStage', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createProjectStage: builder.mutation({
        query: request => ({
          url: 'project_stage',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ProjectStages'],
      }),
      updateProjectStage: builder.mutation<ProjectStage, ProjectStage>({
        query: request => ({
          url: `project_stage/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'ProjectStage', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            project_stage.util.updateQueryData(
              'getProjectStage',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(project_stage.util.invalidateTags(['ProjectStages']))
             */
            dispatch(project_stage.util.invalidateTags(['ProjectStages']));
          }
        },
      }),
      deleteProjectStage: builder.mutation<ProjectStage, number>({
        query: id => ({
          url: `project_stage/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ProjectStage' as const, arg }, 'ProjectStages']
            : ['ProjectStages'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetProjectStagesQuery,
  useGetProjectStageQuery,
  useCreateProjectStageMutation,
  useUpdateProjectStageMutation,
  useDeleteProjectStageMutation,
  usePrefetch: useProjectStagesPrefetch,
} = project_stage;
const selectProjectStagesRoot = project_stage.endpoints.getProjectStages.select(
  {}
);
export const selectProjectStages = createSelector(
  selectProjectStagesRoot,
  project_stage => project_stage.data || []
);
